// Save-to-file + Import for GGC games. A saved game is the SAME /ggGames setup
// payload ({ meta, players } from GGameSetup.buildPayload) wrapped in a small
// envelope with a type + version marker, so Import can validate it before
// re-hydrating it into the setup form (gameToSetupState). This is pure file
// export / re-hydrate — it does NOT touch the sync/settlement logic.

const APP = 'golfhustler'
const TYPE = 'gg-game'
const VERSION = 1

// Filename-safe slug from the game name; always returns something usable.
function safeName(s) {
  const slug = String(s || '').trim().replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 60)
  return slug || 'golf-game'
}

// Wrap the /ggGames payload in the versioned envelope that lands on disk.
export function buildGameFile(meta, players) {
  return { app: APP, type: TYPE, version: VERSION, savedAt: new Date().toISOString(), game: { meta, players } }
}

// Download the game as a JSON file. Uses the File System Access API
// (showSaveFilePicker) so the OS "choose location" dialog appears; falls back to
// a normal <a download> in browsers without it (Safari/Firefox). Returns
// { ok:true, method, filename } on success, { ok:false, cancelled:true } if the
// user dismisses the picker.
export async function saveGameToFile(meta, players) {
  const json = JSON.stringify(buildGameFile(meta, players), null, 2)
  const filename = `${safeName(meta && meta.name)}.golfgame.json`

  if (typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function') {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'Golf Hustler game', accept: { 'application/json': ['.json'] } }],
      })
      const w = await handle.createWritable()
      await w.write(json)
      await w.close()
      return { ok: true, method: 'fs-access', filename: handle.name || filename }
    } catch (e) {
      // User cancelled the OS dialog — report it, don't also trigger a download.
      if (e && (e.name === 'AbortError' || e.name === 'NotAllowedError')) return { ok: false, cancelled: true }
      // Any other failure: fall through to the universal download fallback.
    }
  }

  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return { ok: true, method: 'download', filename }
}

// Validate + parse a picked file's text into the /ggGames record { meta, players }.
// Throws Error with a user-facing message when it isn't a valid GG game file.
export function parseGameFile(text) {
  let data
  try { data = JSON.parse(text) } catch { throw new Error('That file isn’t a valid game file (not readable JSON).') }
  if (!data || data.type !== TYPE) throw new Error('That file isn’t a Golf Hustler game file.')
  if (Number(data.version) > VERSION) throw new Error('That game was saved by a newer version of Golf Hustler. Update the site and try again.')
  const game = data.game || {}
  if (!game.meta || !Array.isArray(game.players) || game.players.length === 0) {
    throw new Error('That game file is missing its game data.')
  }
  return { meta: game.meta, players: game.players }
}

// Open a file picker and resolve with the parsed { meta, players }, or null if
// the user picks nothing. Uses a hidden <input type=file> so it works in every
// browser. Rejects (with a user-facing message) when the file isn't a valid GG
// game file.
export function pickGameFile() {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/json,.json'
    input.style.display = 'none'
    input.onchange = () => {
      const file = input.files && input.files[0]
      input.remove()
      if (!file) { resolve(null); return }
      const reader = new FileReader()
      reader.onload = () => { try { resolve(parseGameFile(String(reader.result || ''))) } catch (e) { reject(e) } }
      reader.onerror = () => reject(new Error('Could not read that file.'))
      reader.readAsText(file)
    }
    document.body.appendChild(input)
    input.click()
  })
}
